import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import 'jspdf-autotable';

export async function generateInsightsPDF(companyName, elementId = 'insights-content') {
  try {
    // Get the content element
    const element = document.getElementById(elementId);
    if (!element) {
      throw new Error('Content element not found');
    }

    // Create canvas from HTML
    const canvas = await html2canvas(element, {
      scale: 2,
      useCORS: true,
      logging: false,
      backgroundColor: '#ffffff'
    });

    // Calculate PDF dimensions
    const imgWidth = 210; // A4 width in mm
    const pageHeight = 297; // A4 height in mm
    const imgHeight = (canvas.height * imgWidth) / canvas.width;
    let heightLeft = imgHeight;
    let position = 0;

    // Create PDF
    const pdf = new jsPDF('p', 'mm', 'a4');

    // Add header
    pdf.setFontSize(20);
    pdf.setTextColor(40);
    pdf.text('AI Adoption Insights Report', 105, 15, { align: 'center' });

    pdf.setFontSize(12);
    pdf.setTextColor(100);
    pdf.text(companyName, 105, 23, { align: 'center' });

    pdf.setFontSize(10);
    pdf.setTextColor(150);
    const date = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    pdf.text(`Generated on ${date}`, 105, 29, { align: 'center' });

    // Add content image
    const imgData = canvas.toDataURL('image/png');
    position = 35; // Start below header

    pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
    heightLeft -= pageHeight - position;

    // Add new pages if content is longer than one page
    while (heightLeft > 0) {
      position = heightLeft - imgHeight + 10;
      pdf.addPage();
      pdf.addImage(imgData, 'PNG', 0, position, imgWidth, imgHeight);
      heightLeft -= pageHeight;
    }

    // Add footer to all pages
    const pageCount = pdf.internal.getNumberOfPages();
    for (let i = 1; i <= pageCount; i++) {
      pdf.setPage(i);
      pdf.setFontSize(8);
      pdf.setTextColor(150);
      pdf.text(
        `Page ${i} of ${pageCount}`,
        105,
        pdf.internal.pageSize.height - 10,
        { align: 'center' }
      );
      pdf.text(
        'SEY AI Insights Platform',
        15,
        pdf.internal.pageSize.height - 10
      );
    }

    // Save PDF
    const fileName = `${companyName.replace(/\s+/g, '_')}_AI_Insights_${new Date().getTime()}.pdf`;
    pdf.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Error generating PDF:', error);
    return { success: false, error: error.message };
  }
}

export async function generateComparisonPDF(companyData, comparisonData, charts = []) {
  try {
    const pdf = new jsPDF('p', 'mm', 'a4');
    let yPosition = 20;

    // Header
    pdf.setFontSize(20);
    pdf.setTextColor(40);
    pdf.text('AI Adoption Analysis', 105, yPosition, { align: 'center' });

    yPosition += 10;
    pdf.setFontSize(14);
    pdf.text(companyData.name, 105, yPosition, { align: 'center' });

    yPosition += 8;
    pdf.setFontSize(10);
    pdf.setTextColor(150);
    const date = new Date().toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
    pdf.text(`Generated on ${date}`, 105, yPosition, { align: 'center' });

    yPosition += 15;

    // Company Overview
    pdf.setFontSize(12);
    pdf.setTextColor(40);
    pdf.text('Company Overview', 15, yPosition);
    yPosition += 8;

    const overviewData = [
      ['Industry', companyData.industry || 'N/A'],
      ['Village', companyData.village || 'N/A'],
      ['Region', companyData.region || 'N/A'],
      ['Survey Responses', companyData.responseCount?.toString() || '0']
    ];

    pdf.autoTable({
      startY: yPosition,
      head: [['Attribute', 'Value']],
      body: overviewData,
      theme: 'grid',
      headStyles: { fillColor: [100, 204, 156] },
      margin: { left: 15, right: 15 }
    });

    yPosition = pdf.lastAutoTable.finalY + 15;

    // Key Metrics
    if (yPosition > 250) {
      pdf.addPage();
      yPosition = 20;
    }

    pdf.setFontSize(12);
    pdf.text('Key Metrics', 15, yPosition);
    yPosition += 8;

    const metricsData = [
      ['Adoption Rate', `${companyData.adoptionRate?.toFixed(1) || 0}%`],
      ['Adoption Level', `${companyData.adoptionLevel?.toFixed(1) || 0}/100`],
      ['ATI Score', `${companyData.atiScore?.toFixed(1) || 0}/100`],
      ['AI Knowledge', `${companyData.aiKnowledge?.toFixed(2) || 0}/5`],
      ['Optimism Score', `${companyData.optimismScore?.toFixed(1) || 0}/10`],
      ['AI Trust', `${companyData.aiTrust?.toFixed(1) || 0}/10`],
      ['AI Fear', `${companyData.aiFear?.toFixed(1) || 0}/10`],
      ['Learning Interest', `${companyData.learningInterest?.toFixed(1) || 0}%`]
    ];

    pdf.autoTable({
      startY: yPosition,
      head: [['Metric', 'Value']],
      body: metricsData,
      theme: 'grid',
      headStyles: { fillColor: [100, 204, 156] },
      margin: { left: 15, right: 15 }
    });

    // Add charts if provided
    for (const chart of charts) {
      pdf.addPage();
      yPosition = 20;

      pdf.setFontSize(12);
      pdf.text(chart.title, 15, yPosition);

      if (chart.image) {
        pdf.addImage(chart.image, 'PNG', 15, yPosition + 5, 180, 120);
      }
    }

    // Save PDF
    const fileName = `${companyData.name.replace(/\s+/g, '_')}_Analysis_${new Date().getTime()}.pdf`;
    pdf.save(fileName);

    return { success: true, fileName };
  } catch (error) {
    console.error('Error generating PDF:', error);
    return { success: false, error: error.message };
  }
}
